import { FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { gsap } from "gsap";

import { ISequencePlay, Sequence } from "./Sequence";
import { SpinePlayConfig, SpinePlayer } from "./SpinePlayer";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { TextStyleOptions } from "../setting/text/IText";

enum playerType {
    TEXT = "String",
    SPINE = "Spine",
    SEQUENCE = "Sequence"
}

interface UniObject {
    name: string;
    type: playerType;
    obj: SpinePlayer | Sequence | PIXI.Text;
}

export interface UniPlayer {
    obj: SpinePlayer | Sequence | PIXI.Text;
    play: (conf?: ISequencePlay | SpinePlayConfig) => gsap.core.Timeline;
    stop: (playEnd?: boolean) => void;
}

export class UniversalPlayer {

    private missingAnimTxt: string = "Missing: ";

    private readonly MISSING_TEXT_STYLE: TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 10,
        dropShadowDistance: 2,
        fontFamily: "Open Sans",
        fontWeight: FontWeight.EXTRA_BOLD,
        align: "center",
        fontSize: 46,
        fill: [ "white" ]
    };

    private _uniPlayer: UniObject;
    private _uniPlayers: UniObject[] = [];

    private static assetLoader: AssetLoader;

    public static setLoader(_assetLoader: AssetLoader): void { 
        if (!this.assetLoader) this.assetLoader = _assetLoader;
        if(SpinePlayer) SpinePlayer.setSpineLoader(_assetLoader);
        if(Sequence) Sequence.setAssetLoader(_assetLoader)
    }

    constructor(objTypeNames?: string[] | string) {
        if (!UniversalPlayer.assetLoader)
            throw ("Need to set assetLoader before usage. 'UniversalPlayer.setLoader(loader)' ")

        if (objTypeNames) this.load(objTypeNames);
    }

    public load(objTypeNames: string[] | string): void {
        if (Array.isArray(objTypeNames))
            objTypeNames.forEach(nType => {
                if (!this._uniPlayers.find(t => t.name == nType))
                    this._uniPlayers.push(this.define(nType))
            });
        else if (!this._uniPlayers.find(t => t.name == objTypeNames))
            this._uniPlayers.push(this.define(objTypeNames));
    }

    public unLoad(objTypeNames?: string[] | string): void {
        if (objTypeNames && !Array.isArray(objTypeNames)) objTypeNames = [objTypeNames];

        this._uniPlayers.forEach((o, i) => {
            if (!objTypeNames || objTypeNames.indexOf(o.name) >= 0) {
                o.obj.destroy({ children: true })
                delete this._uniPlayers[i];
            }
        });

        // Remove deleted posts from array;
        this._uniPlayers = this._uniPlayers.filter(o => o);
    }

    public get uniPlayers(): UniObject[] { return this._uniPlayers };

    private define(typeName: string): UniObject {
        try {
            let spine = new SpinePlayer(typeName);
            return { name: typeName, obj: spine, type: playerType.SPINE };
        } catch (e) {
            debugger;
            if (PIXI.utils.TextureCache[typeName]) {
                const seq: Sequence = new Sequence(UniversalPlayer.assetLoader.getTextures(typeName));
                seq.anchor.set(0.5);
                return { name: typeName, obj: seq, type: playerType.SEQUENCE };
            }
            else {
                const text: PIXI.Text = this.createText(this.missingAnimTxt + typeName, this.MISSING_TEXT_STYLE);
                return { name: typeName, obj: text, type: playerType.TEXT };
            }
        }
    }

    private createText(text: string, style: TextStyleOptions): PIXI.Text {
        const textField = new PIXI.Text(text, style);
        textField.anchor.set(0.5, 0.5);
        return textField;
    }

    public get(_name: string): UniPlayer {
        this._uniPlayer = this._uniPlayers.find((o) => { return o.name == _name })!;

        if (!this._uniPlayer) throw ("Couldn't find loaded animation: " + _name);

        return { obj: this._uniPlayer.obj, 
            play:(conf?:ISequencePlay | SpinePlayConfig) => {
                switch (this._uniPlayer.type) {
                    case playerType.SEQUENCE:
                        return (<Sequence>this._uniPlayer.obj).play(conf as ISequencePlay);
                    case playerType.SPINE:
                        return (<SpinePlayer>this._uniPlayer.obj).play(conf as SpinePlayConfig);
                    default:
                        return gsap.timeline().to(this._uniPlayer.obj, { duration: conf ? conf.duration : 0.5 });
                }
            }
            , stop: (playEnd?:boolean) => {
                switch (this._uniPlayer.type) {
                    case playerType.SEQUENCE:
                        (<Sequence>this._uniPlayer.obj).stop(playEnd);
                        break;
                    case playerType.SPINE:
                        (<SpinePlayer>this._uniPlayer.obj).stop(playEnd);
                        break;
                    default:
                        break;
                }
            }
        };
    }
}
